import { useNavigate, useParams } from "react-router-dom";
import { mutate } from "swr";
import { useEffect, useState } from "react";

import { apiFetchWithAuth, exchangeSingleUseAuthCode } from "../../../../api";
import useLocation from "../../../../hooks/useLocation";
import { ApiCompetition } from "../../../../types";
import EmailScreen from "./EmailScreen/EmailScreen";
import LoginScreen from "./LoginScreen/LoginScreen";
import RegisterScreen from "./RegisterScreen/RegisterScreen";
import useCompetition from "../../../../hooks/useCompetition";
import { getIsLoggedIn } from "../../../../services/auth";
import { removeAuthCodeFromUrl } from "../hooks/useAuthWall";

export const JudgeEmailScreen = () => {
  const navigate = useNavigate();
  const state = useLocation<{ next: string }>().state;
  const nextPath = state?.next;

  const { competitionUUID } = useParams();

  const [isWaitingForLogin, setIsWaitingForLogin] = useState(false);

  useEffect(() => {
    const queryParams = new URLSearchParams(window.location.search);
    const authCode = queryParams.get("authCode");

    if (!authCode && getIsLoggedIn()) {
      // If the user is already logged in when they hit this screen,
      // redirect them immediately to the confirm screen
      navigate("../confirm", { state: { didJustRegister: false } });
    }

    if (authCode) {
      const currentUrl =
        queryParams.get("authCodeUrl") ||
        removeAuthCodeFromUrl(window.location.toString());
      setIsWaitingForLogin(true);
      exchangeSingleUseAuthCode(authCode, currentUrl)
        .then(() => {
          mutate("/profiles/me");
          mutate(`/competitions/${competitionUUID}`);
          if (nextPath) {
            navigate(nextPath);
          } else {
            navigate(`/${competitionUUID}`);
          }
        })
        .catch(() => {
          setIsWaitingForLogin(false);
        });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const onNext = ({
    email,
    emailExists,
  }: {
    email: string;
    emailExists: boolean;
  }) => {
    if (emailExists) {
      navigate("../login", {
        state: {
          email,
          next: nextPath,
        },
      });
    } else {
      navigate("../register", { state: { email } });
    }
  };

  return (
    <EmailScreen
      loading={isWaitingForLogin}
      introText="Please enter your email address"
      onNext={onNext}
    />
  );
};

export const JudgeLoginScreen = () => {
  const navigate = useNavigate();
  const params = useParams();
  const state = useLocation<{ next: string }>().state;
  const nextPath = state?.next;

  const onLogin = async () => {
    const competition = await apiFetchWithAuth<ApiCompetition>(
      `/competitions/${params.competitionUUID}`
    );
    mutate(`/competitions/${competition.uuid}`, competition);
    if (
      competition.role === "MASTER" ||
      competition.role === "STAFF_JUDGE" ||
      competition.role === "JUDGE" ||
      (competition.role === "STAFF" && nextPath)
    ) {
      if (nextPath) {
        navigate(nextPath);
      } else {
        navigate(`/${params.competitionUUID}`);
      }
      return;
    }
    if (
      !competition.settings.judgesCanBeParents ||
      competition.childEntries.length
    ) {
      // If judges can't be parents or there are already child entries, go to the confirm screen
      navigate("../confirm", { state: { didJustRegister: false } });
    } else {
      navigate("../kids", { state: { didJustRegister: false } });
    }
  };

  return <LoginScreen onLogin={onLogin} />;
};

export const JudgeRegisterScreen = () => {
  const navigate = useNavigate();
  const { competitionUUID } = useParams();
  const { competition } = useCompetition(competitionUUID);

  const onRegister = () => {
    if (competition?.settings.judgesCanBeParents) {
      navigate("../kids", { state: { didJustRegister: true } });
    } else {
      navigate("../confirm", { state: { didJustRegister: true } });
    }
  };

  return <RegisterScreen isJudge onRegister={onRegister} />;
};
